"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { X, Check } from "lucide-react"
import Image from "next/image"
import LogoHeader from "./functions/header/logo-header"

interface ProcessingScreenProps {
  externalStep?: number
  externalStepsNumber?: number
  onCancel?: () => void
  onNavigateToHome?: () => void
}

// Fake progress animation constants
const INITIAL_JUMP_TARGET = 50      // Quick rise to 50%
const INITIAL_JUMP_DURATION = 1500  // 1.5 seconds to reach 50%
const SLOW_INCREMENT_CAP = 90       // Cap at 90% during processing
const SLOW_INCREMENT_INTERVAL = 1000 // 1% per second
const COMPLETION_ANIMATION_DURATION = 500 // 0.5s to animate to 100%

export default function ProcessingScreen({
  externalStep,
  externalStepsNumber,
  onCancel,
  onNavigateToHome
}: ProcessingScreenProps = {}) {
  // Fake progress state - simple and predictable
  const [fakeProgress, setFakeProgress] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const [shouldAnimate100, setShouldAnimate100] = useState(false)
  const completionStartProgressRef = useRef<number>(0)

  // Stable step labels - familiar and predictable for users
  const stepLabels = [
    "Analizujemy obraz",
    "Wykrywamy tekst",
    "Rozpoznajemy znaki",
    "Przetwarzamy tekst",
    "Nakładamy formatowanie",
  ]

  // Check if external mode (multi-page with step tracking)
  const isExternalMode = externalStep !== undefined && externalStepsNumber !== undefined

  // Simple fake progress - just use the animated value
  const finalProgress = fakeProgress

  // Calculate which step should be active based on progress
  const getCurrentStepIndex = (progress: number) => {
    const stepPercentage = 100 / stepLabels.length
    return Math.min(Math.floor(progress / stepPercentage), stepLabels.length - 1)
  }

  const currentStepIndex = getCurrentStepIndex(finalProgress)

  // Generate steps array based on current progress
  const steps = stepLabels.map((label, index) => {
    if (finalProgress >= 100) {
      return { label, completed: true, inProgress: false }
    }
    if (index < currentStepIndex) {
      return { label, completed: true, inProgress: false }
    } else if (index === currentStepIndex) {
      return { label, completed: false, inProgress: true }
    } else {
      return { label, completed: false, inProgress: false }
    }
  })

  // Effect 1: Initial jump animation (0% → 50% over 1.5s)
  useEffect(() => {
    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min((elapsed / INITIAL_JUMP_DURATION) * INITIAL_JUMP_TARGET, INITIAL_JUMP_TARGET)
      setFakeProgress(progress)
      
      if (progress >= INITIAL_JUMP_TARGET) {
        clearInterval(interval)
      }
    }, 50) // Update every 50ms for smooth animation
    
    return () => clearInterval(interval)
  }, [])

  // Effect 2: Slow increment phase (50% → 90% at 1% per second)
  useEffect(() => {
    if (fakeProgress >= INITIAL_JUMP_TARGET && fakeProgress < SLOW_INCREMENT_CAP && !shouldAnimate100) {
      const interval = setInterval(() => {
        setFakeProgress(prev => Math.min(prev + 1, SLOW_INCREMENT_CAP))
      }, SLOW_INCREMENT_INTERVAL)
      
      return () => clearInterval(interval)
    }
  }, [fakeProgress, shouldAnimate100])

  // Effect 3: Detect completion and trigger 100% animation
  useEffect(() => {
    const isComplete = isExternalMode && externalStep !== undefined && externalStepsNumber !== undefined && externalStep >= externalStepsNumber
    if (isComplete && !isCompleted) {
      setIsCompleted(true)
      completionStartProgressRef.current = fakeProgress
      setShouldAnimate100(true)
    }
  }, [isExternalMode, externalStep, externalStepsNumber, isCompleted, fakeProgress])

  // Effect 4: Animate to 100% on completion
  useEffect(() => {
    if (shouldAnimate100) {
      const startProgress = completionStartProgressRef.current
      const startTime = Date.now()
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime
        const progressDelta = (elapsed / COMPLETION_ANIMATION_DURATION) * (100 - startProgress)
        const newProgress = Math.min(startProgress + progressDelta, 100)
        setFakeProgress(newProgress)
        
        if (newProgress >= 100) {
          clearInterval(interval)
          // Navigation will happen in parent component after delay
        }
      }, 16) // ~60fps for smooth animation
      
      return () => clearInterval(interval)
    }
  }, [shouldAnimate100])

  // Simple, stable status text
  const getStatusText = () => {
    if (!isExternalMode || externalStep === undefined || externalStepsNumber === undefined) {
      return null
    }
    
    if (externalStepsNumber === 1) {
      return null // Don't show "1 z 1" - not helpful
    }
    
    // For multi-page documents, show page progress
    const currentPage = Math.min(externalStep + 1, externalStepsNumber)
    return `Strona ${currentPage} z ${externalStepsNumber}`
  }

  const statusText = getStatusText()

  return (
    <div className="min-h-screen bg-[rgba(251,248,255,1)] flex flex-col">
      <LogoHeader onLogoClick={onNavigateToHome} />
      <main className="flex-1 flex justify-center p-4 pt-[30px] md:pt-[150px] items-start">
        <div className="w-full max-w-md space-y-8">
          {/* Title */}
          <h2 className="text-4xl font-bold text-[rgba(30,49,68,1)] text-center">Przetwarzamy Twój tekst</h2>

          {/* Progress Bar - with subtle shimmer to show activity */}
          <div className="space-y-4">
            <div className="relative">
              <Progress value={finalProgress} className="w-full h-3" />
              {/* Subtle shimmer overlay - only visible during processing */}
              {finalProgress < 100 && (
                <div className="absolute inset-0 overflow-hidden rounded-full pointer-events-none">
                  <div className="absolute inset-0 w-1/4 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-progress-shimmer" />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center">
              <p className="text-base font-medium text-[rgba(78,85,98,1)] text-center">
                {Math.round(finalProgress)}% zakończone
              </p>
              <Image
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/decor-h2sQg79DH0kxPZ8r8QpmOtDtM10apx.png"
                alt=""
                width={130}
                height={50}
                className="w-[130px] h-auto -mt-[10px]"
              />
            </div>
          </div>

          {/* Page progress for multi-page documents - simple text, no animation */}
          {statusText && (
            <p className="text-sm text-center text-[rgba(78,85,98,1)]">{statusText}</p>
          )}

          {/* Steps Checklist - stable, predictable list with consistent positioning */}
          <div className="space-y-2 text-left">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex items-center gap-3 p-4 rounded-lg transition-colors duration-500 ${
                  step.inProgress ? "bg-[#FFFFFF]" : "bg-transparent"
                }`}
                style={
                  step.inProgress
                    ? {
                        backgroundImage: `
                          repeating-linear-gradient(0deg, #ECECEF 0, #ECECEF 4px, transparent 4px, transparent 8px),
                          repeating-linear-gradient(90deg, #ECECEF 0, #ECECEF 4px, transparent 4px, transparent 8px),
                          repeating-linear-gradient(180deg, #ECECEF 0, #ECECEF 4px, transparent 4px, transparent 8px),
                          repeating-linear-gradient(270deg, #ECECEF 0, #ECECEF 4px, transparent 4px, transparent 8px)
                        `,
                        backgroundSize: "1px 100%, 100% 1px, 1px 100%, 100% 1px",
                        backgroundPosition: "0 0, 0 0, 100% 0, 0 100%",
                        backgroundRepeat: "no-repeat",
                      }
                    : undefined
                }
              >
                {/* Fixed-width icon container for consistent text alignment */}
                <div className="w-5 flex items-center justify-center flex-shrink-0">
                  {step.completed ? (
                    <Check className="size-5 text-[rgba(57,192,113,1)]" />
                  ) : step.inProgress ? (
                    <div className="size-3 rounded-full bg-[rgba(99,102,241,1)] animate-pulse-gentle" />
                  ) : (
                    <div className="size-2 rounded-full bg-[rgba(78,85,98,1)]" />
                  )}
                </div>
                <span
                  className={`text-base ${
                    step.completed || step.inProgress
                      ? "text-[rgba(30,49,68,1)] font-medium"
                      : "text-[rgba(109,114,127,1)]"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* Stay-put notice. The OCR request dies if iOS suspends the page, and
              measured jobs run to ~36 s, so the wait needs an explanation and the
              one instruction that actually protects the job. */}
          <div
            role="note"
            className="rounded-lg border border-[rgba(99,102,241,0.35)] bg-[rgba(99,102,241,0.06)] p-4"
          >
            <p className="text-base font-medium text-[rgba(30,49,68,1)]">
              Nie zamykaj tego ekranu
            </p>
            <p className="mt-1 text-sm text-[rgba(78,85,98,1)]">
              Rozpoznawanie tekstu może potrwać nawet minutę. Jeśli zablokujesz telefon
              lub przejdziesz do innej aplikacji, przetwarzanie zostanie przerwane.
            </p>
          </div>

          {/* Cancel Button */}
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              className="w-auto px-6 rounded-full border-[rgba(109,114,127,1)] text-[rgba(78,85,98,1)] hover:bg-[rgba(236,236,239,0.5)] bg-transparent"
              onClick={onCancel}
            >
              <X className="size-4 mr-2" />
              Anuluj przetwarzanie
            </Button>
          </div>
        </div>
      </main>
    </div>
  )
}
